/**
 * CatererServicePriceCard — items × persons surcharge grid.
 *
 * Rows ("no. of items per plate") are AUTO-DERIVED from the dish counts of
 * the vendor's saved menu variants — the vendor no longer adds/removes rows
 * manually. They only:
 *   - control the columns (no. of persons brackets)
 *   - fill the cell prices
 *
 * Customer-side: per-meal listing price = (cheapest menu variant price/plate)
 *                                       + (matrix cell at closest item-tier
 *                                          for that variant's dish count
 *                                          × closest guest bracket).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { GridFour, FloppyDisk, Info, Warning, Plus, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STYLE_META = {
  traditional: { label: 'Traditional Serving (with leaf)', tone: 'orange' },
  buffet: { label: 'Buffet', tone: 'amber' },
};

const DEFAULT_BRACKETS = ['50', '150', '200', '250', '300', '400'];

const uniqueAppendGuests = (list) => {
  const nums = list.map((x) => parseInt(x, 10)).filter((n) => !Number.isNaN(n));
  const last = nums.length ? Math.max(...nums) : 0;
  let candidate = String(last + 50 || 100);
  let i = 0;
  while (list.includes(candidate) && i < 50) {
    candidate = String(parseInt(candidate, 10) + 50);
    i += 1;
  }
  return candidate;
};

const CatererServicePriceCard = () => {
  const [styles, setStyles] = useState([]);
  // Item tiers (rows) are now READ-ONLY — derived from menu variants.
  const [tiers, setTiers] = useState([]);
  const [brackets, setBrackets] = useState(DEFAULT_BRACKETS);
  const [matrix, setMatrix] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);
  // Refresh tiers when the user navigates back to this card (e.g. after
  // saving a menu variant) — small interval keeps the grid in sync.
  useEffect(() => {
    const id = setInterval(() => { load(); }, 8000);
    return () => clearInterval(id);
  }, []);

  const load = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/me/caterer-plate-matrix`, { withCredentials: true });
      setStyles(data.styles || []);
      setTiers(data.item_tiers || []);
      setBrackets((data.guest_brackets && data.guest_brackets.length) ? data.guest_brackets : DEFAULT_BRACKETS);
      setMatrix(data.matrix || {});
    } catch {
      // Not a caterer or no data yet — silently empty.
    }
  };

  const setCell = (style, tier, bracket, value) => {
    setMatrix((cur) => {
      const next = { ...cur };
      next[style] = { ...(next[style] || {}) };
      next[style][tier] = { ...(next[style][tier] || {}) };
      next[style][tier][bracket] = value;
      return next;
    });
  };

  const cellValue = (style, tier, bracket) => {
    const v = matrix?.[style]?.[tier]?.[bracket];
    return v == null ? '' : String(v);
  };

  // ----- Column (no. of persons) ops -----
  const addCol = () => {
    setBrackets((cur) => [...cur, uniqueAppendGuests(cur)]);
  };
  const removeCol = (label) => {
    if (brackets.length <= 1) { toast.error('Keep at least one column'); return; }
    setBrackets((cur) => cur.filter((x) => x !== label));
    setMatrix((cur) => {
      const next = { ...cur };
      Object.keys(next).forEach((style) => {
        Object.keys(next[style] || {}).forEach((tier) => {
          if (next[style][tier] && next[style][tier][label] !== undefined) {
            const { [label]: _, ...rest } = next[style][tier];
            next[style][tier] = rest;
          }
        });
      });
      return next;
    });
  };
  const renameCol = (idx, newLabel) => {
    const trimmed = (newLabel || '').toString().trim().slice(0, 10);
    setBrackets((cur) => {
      if (!trimmed) return cur;
      const next = [...cur];
      const old = next[idx];
      next[idx] = trimmed;
      if (old !== trimmed) {
        setMatrix((m) => {
          const out = { ...m };
          Object.keys(out).forEach((style) => {
            Object.keys(out[style] || {}).forEach((tier) => {
              if (out[style][tier] && out[style][tier][old] !== undefined) {
                const { [old]: oldVal, ...rest } = out[style][tier];
                out[style][tier] = { ...rest, [trimmed]: oldVal };
              }
            });
          });
          return out;
        });
      }
      return next;
    });
  };

  const save = async () => {
    const dupCols = new Set(brackets).size !== brackets.length;
    if (dupCols) { toast.error('Column labels must be unique'); return; }
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${BACKEND_URL}/api/me/caterer-plate-matrix`,
        { matrix, guest_brackets: brackets },
        { withCredentials: true },
      );
      setMatrix(data.matrix || {});
      if (data.item_tiers) setTiers(data.item_tiers);
      if (data.guest_brackets) setBrackets(data.guest_brackets);
      const cells = Object.values(data.matrix || {}).reduce(
        (a, st) => a + Object.values(st).reduce((b, t) => b + Object.keys(t).length, 0), 0,
      );
      toast.success(`Saved ${cells} price${cells === 1 ? '' : 's'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  if (styles.length === 0) {
    return (
      <Card className="bg-white border-[#E8E1D5] mt-6" data-testid="service-price-card-empty">
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <GridFour size={20} className="text-brand-primary" /> Service Price
          </CardTitle>
          <CardDescription>
            Tick at least one serving style above (Traditional / Buffet) before setting prices.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 px-3 py-2 rounded text-xs text-amber-800">
            <Warning size={14} className="shrink-0 mt-0.5" />
            <span>No serving style ticked yet. Customers can't see your caterer until at least one is selected and at least one price is filled.</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-[#E8E1D5] mt-6" data-testid="service-price-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <GridFour size={20} className="text-brand-primary" /> Service Price
        </CardTitle>
        <CardDescription>
          For each serving style, set your per-plate <span className="font-semibold">service surcharge</span> at
          each combination of items per plate (rows) and guest count (columns).
          <span className="block mt-1 text-[11px]">
            <span className="font-semibold">Rows are derived automatically</span> from the dish counts of your saved
            menus — to add a new row, add a menu with that many dishes. Columns (no. of persons) are editable below.
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        {tiers.length === 0 && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 px-3 py-2 rounded text-xs text-amber-800" data-testid="service-price-no-menus">
            <Warning size={14} className="shrink-0 mt-0.5" />
            <span>Add at least one menu (Tiffin / Lunch / Snacks / Dinner) with dishes — rows are auto-generated from the number of dishes in each menu.</span>
          </div>
        )}

        {tiers.length > 0 && styles.map((style) => {
          const meta = STYLE_META[style] || { label: style, tone: 'gray' };
          return (
            <div key={style} data-testid={`service-price-${style}`}>
              <h4 className={`text-sm font-bold uppercase tracking-wider mb-2 inline-block px-2 py-1 rounded ${
                meta.tone === 'orange' ? 'bg-orange-100 text-orange-800' :
                meta.tone === 'amber' ? 'bg-amber-100 text-amber-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {meta.label}
              </h4>
              <div className="overflow-x-auto border border-[#E8E1D5] rounded">
                <table className="w-full text-sm" data-testid={`service-price-table-${style}`}>
                  <thead>
                    <tr className="bg-sand-50 border-b border-[#E8E1D5]">
                      <th className="text-left px-2 py-2 text-[10px] uppercase font-bold tracking-wider text-text-secondary min-w-[140px]">
                        No. items ↓ / Persons →
                      </th>
                      {brackets.map((b, ci) => (
                        <th key={`${b}-${ci}`} className="px-1 py-2 text-center min-w-[90px]"
                          data-testid={`service-col-${style}-${b}`}>
                          <div className="flex items-center gap-1 justify-center">
                            <input
                              type="text"
                              value={b}
                              onChange={(e) => renameCol(ci, e.target.value)}
                              className="w-14 h-7 px-1 text-center text-xs font-bold border border-[#E8E1D5] rounded focus:border-brand-primary focus:outline-none bg-white"
                              data-testid={`service-col-input-${style}-${ci}`}
                            />
                            <button
                              type="button"
                              onClick={() => removeCol(b)}
                              className="text-text-secondary hover:text-red-600 p-0.5"
                              title="Remove column"
                              data-testid={`service-col-remove-${style}-${ci}`}
                            >
                              <X size={12} weight="bold" />
                            </button>
                          </div>
                        </th>
                      ))}
                      <th className="px-1 py-2 min-w-[80px]">
                        <button
                          type="button"
                          onClick={addCol}
                          className="flex items-center gap-1 mx-auto text-[10px] uppercase font-bold tracking-wider text-brand-primary hover:text-[#A84A38] border border-dashed border-brand-primary/40 rounded px-2 py-1"
                          data-testid={`service-add-col-${style}`}
                        >
                          <Plus size={12} weight="bold" /> Col
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tiers.map((tier) => (
                      <tr key={tier} className="border-b border-[#E8E1D5]/60">
                        <td className="px-2 py-1.5" data-testid={`service-row-${style}-${tier}`}>
                          <div className="flex items-center gap-1.5">
                            <span className="inline-block min-w-[36px] h-7 px-2 py-1 text-center text-xs font-bold rounded bg-sand-100 border border-[#E8E1D5] text-text-primary">
                              {tier}
                            </span>
                            <span className="text-xs text-text-secondary">items</span>
                          </div>
                        </td>
                        {brackets.map((bracket) => (
                          <td key={bracket} className="px-1 py-1">
                            <input
                              type="number"
                              min={0}
                              value={cellValue(style, tier, bracket)}
                              onChange={(e) => setCell(style, tier, bracket, e.target.value)}
                              placeholder="—"
                              className="w-full h-8 px-1 text-center text-xs border border-[#E8E1D5] rounded focus:border-brand-primary focus:outline-none"
                              data-testid={`service-cell-${style}-${tier}-${bracket}`}
                            />
                          </td>
                        ))}
                        <td />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}

        {tiers.length > 0 && (
          <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 px-3 py-2 rounded text-xs text-blue-800">
            <Info size={14} className="shrink-0 mt-0.5" />
            <span>
              This is a <span className="font-bold">per-plate surcharge</span> added on top of your menu's plate
              price. Customers see: <span className="font-mono">menu ₹/plate + service-price[items × persons]</span>.
              E.g., menu ₹120 + service-price[17 items × 200 persons]=₹250 → customer pays ₹370/plate.
            </span>
          </div>
        )}

        {tiers.length > 0 && (
          <div className="flex items-center justify-end">
            <Button onClick={save} disabled={saving}
              className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50 disabled:opacity-60"
              data-testid="service-price-save-btn">
              <FloppyDisk size={16} className="mr-2" weight="fill" />
              {saving ? 'Saving…' : 'Save Service Price'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CatererServicePriceCard;
